// Saved filters chip row. localStorage-backed (drillcut.planner is a
// Phase-1 stand-in; Drillcut's roadmap migrates this to a server
// preferences endpoint). Each saved filter snapshots the current
// view's PlannerListFilterState plus a label; clicking a chip
// re-applies it; the + chip captures the current state into a new
// saved filter; × removes a chip.

import { useEffect, useState } from "react";
import { Bookmark, Plus, X } from "lucide-react";
import { DC } from "../sheets/createSlideOver/primitives";
import { PLANNER_SAVED_FILTERS_STORAGE_KEY } from "../../../../../../shared/team-planner/const";
import type { PlannerListFilterState } from "./PlannerListFilters";

interface SavedFilter {
  id: string;
  label: string;
  view: "rocks" | "todos" | "opportunities";
  state: PlannerListFilterState;
}

function loadAll(): SavedFilter[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(PLANNER_SAVED_FILTERS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedFilter[]) : [];
  } catch {
    return [];
  }
}

function saveAll(filters: SavedFilter[]) {
  try {
    window.localStorage.setItem(
      PLANNER_SAVED_FILTERS_STORAGE_KEY,
      JSON.stringify(filters),
    );
  } catch {
    /* quota / privacy mode — silently drop */
  }
}

interface PlannerSavedFiltersProps {
  view: "rocks" | "todos" | "opportunities";
  current: PlannerListFilterState;
  onApply: (state: PlannerListFilterState) => void;
}

export function PlannerSavedFilters({
  view,
  current,
  onApply,
}: PlannerSavedFiltersProps) {
  const [all, setAll] = useState<SavedFilter[]>([]);
  useEffect(() => {
    setAll(loadAll());
  }, []);
  const scoped = all.filter((s) => s.view === view);

  function capture() {
    const label = window.prompt("Save filter as…");
    if (!label?.trim()) return;
    const next: SavedFilter = {
      id: typeof crypto !== "undefined" ? crypto.randomUUID() : String(Date.now()),
      label: label.trim(),
      view,
      state: current,
    };
    const updated = [...all, next];
    setAll(updated);
    saveAll(updated);
  }
  function remove(id: string) {
    const updated = all.filter((s) => s.id !== id);
    setAll(updated);
    saveAll(updated);
  }

  const isActive = (s: SavedFilter) =>
    JSON.stringify(s.state) === JSON.stringify(current);

  return (
    <div className="tp-saved-filters">
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          fontSize: 11,
          color: DC.grey,
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          fontWeight: 700,
        }}
      >
        <Bookmark size={11} /> Saved
      </span>
      {scoped.map((s) => {
        const active = isActive(s);
        return (
          <span
            key={s.id}
            className="tp-saved-filter"
            style={{
              borderColor: active ? DC.primary : undefined,
              color: active ? DC.primary : undefined,
              cursor: "pointer",
            }}
          >
            <button
              type="button"
              onClick={() => onApply(s.state)}
              style={{
                border: 0,
                background: "transparent",
                cursor: "pointer",
                color: "inherit",
                font: "inherit",
                padding: 0,
              }}
            >
              {s.label}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove(s.id);
              }}
              aria-label={`Remove ${s.label}`}
              style={{
                display: "inline-flex",
                width: 14,
                height: 14,
                alignItems: "center",
                justifyContent: "center",
                border: 0,
                background: "transparent",
                cursor: "pointer",
                color: "inherit",
              }}
            >
              <X size={10} />
            </button>
          </span>
        );
      })}
      <button
        type="button"
        onClick={capture}
        className="tp-saved-filter"
        style={{ borderStyle: "dashed" }}
        title="Save current filter"
      >
        <Plus size={12} /> Save current
      </button>
    </div>
  );
}
